import { memo, useCallback, useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { useAutoSelectInfo } from 'shared-modules/services/hooks/autoSelectMainViewLogic';
import { useSelectedStrategyGroup, useAccountInfo } from 'shared-modules/hooks';
import { navigateToRecommendSelectionEnd } from 'shared-modules/redux/actions/autoSelectActions';
import { getPortfoliosRequest } from 'shared-modules/redux/actions/omakasePlanActions';
import { useAutoSelectVisibleFlg, useOmakasePlanMasterLoad } from 'shared-modules/services/hooks/omakasePlanLogic';
import { OMAKASE_PLAN_MASTER_LOAD_ERROR } from 'shared-modules/constants/errorMesages';
import { useCheckAndNotifyAvailableService } from 'shared-modules/hooks/service';
import { FX } from 'shared-modules/constants';
import { useAppHeaderHeight } from '../../../../hooks';
import { Dropdown, ItemButtons, Spin, Tabs } from '../../../../components';
import { CardItem } from '../CardItem';
import styles from './sideMenu.module.scss';
import { CardDescription } from '../CardDescription';
import { FilterCondition } from '../FilterCondition';
import { SearchButton } from '../SearchButton';
import { openConfirmationModal, openOmakasePlanModal } from '../../../../redux/actions/modalActions';
import OmakasePlanBanner from '../OmakasePlanBanner/OmakasePlanBanner';

export const SideMenu = memo(() => {
  const headerHeight = useAppHeaderHeight();
  const autoSelectData = useAutoSelectInfo();
  const [tabWidth, setTabWidth] = useState(115);

  const dispatch = useDispatch();

  const selectedParentTag = autoSelectData.tabs.get;
  const selectedChildTag = autoSelectData.sort.get;
  const showOmakasePlanBanner = useAutoSelectVisibleFlg(selectedParentTag, selectedChildTag);

  const { isNavigatingToRecommendSelection, parentTagId, childrenTagId, selectionInfo } = useSelector(
    (state) => state.autoSelect.recommendSelection,
  );

  const [strategyGroup, setStrategyGroup] = useSelectedStrategyGroup();

  //  オートセレクト用のマスターデータを取得
  useEffect(() => {
    dispatch(getPortfoliosRequest());
  }, [dispatch]);
  const { isReadyStart, onClick, isLoading: omakasePlanIsLoading } = useOmakasePlanMasterLoad();

  const bannerClickSuccessCallback = useCallback(() => {
    dispatch(openOmakasePlanModal());
  }, [dispatch]);

  const bannerClickFailureCallback = useCallback(() => {
    dispatch(
      openConfirmationModal({
        title: OMAKASE_PLAN_MASTER_LOAD_ERROR.title,
        bodyText: OMAKASE_PLAN_MASTER_LOAD_ERROR.body,
        buttonNextText: OMAKASE_PLAN_MASTER_LOAD_ERROR.buttonText,
        isOverlap: true,
      }),
    );
  }, [dispatch]);

  const accountInfo = useAccountInfo();
  const checkAndNotify = useCheckAndNotifyAvailableService();
  const handleBannerClick = useCallback(() => {
    const sent = checkAndNotify(accountInfo, FX);
    if (sent) {
      return;
    }
    onClick({
      successCallback: bannerClickSuccessCallback,
      failureCallback: bannerClickFailureCallback,
    });
  }, [accountInfo, checkAndNotify, onClick, bannerClickSuccessCallback, bannerClickFailureCallback]);

  // レコメンドモーダルのセレクトを謳歌して移動してきた場合、選択されたセレクトのある親タグ、子タグを選択し、
  // 対象のセレクトが選択状態で表示する
  useEffect(() => {
    if (!isNavigatingToRecommendSelection) return;
    if (autoSelectData.tabs.get !== parentTagId) {
      autoSelectData.tabs.set(parentTagId);
      return;
    }
    if (autoSelectData.sort.get !== childrenTagId) {
      autoSelectData.sort.set(childrenTagId);
      return;
    }
    if (!autoSelectData.isLoading && selectionInfo.selectionId !== strategyGroup?.selectionId) {
      setStrategyGroup(selectionInfo);
      return;
    }
    if (
      autoSelectData.tabs.get === parentTagId &&
      autoSelectData.sort.get === childrenTagId &&
      selectionInfo.selectionId === strategyGroup?.selectionId &&
      !autoSelectData.isLoading
    )
      dispatch(navigateToRecommendSelectionEnd());
  }, [
    isNavigatingToRecommendSelection,
    parentTagId,
    childrenTagId,
    autoSelectData.isLoading,
    strategyGroup?.selectionId,
    dispatch,
    autoSelectData.sort,
    autoSelectData.tabs,
    selectionInfo,
    setStrategyGroup,
  ]);

  const selectionItems = useMemo(() => {
    if (autoSelectData.isLoading || omakasePlanIsLoading) {
      return <Spin className={styles.loader} />;
    }
    return (
      <>
        {showOmakasePlanBanner && isReadyStart && <OmakasePlanBanner onClick={handleBannerClick} />}
        {autoSelectData.selections.data.map((item) => (
          <CardItem key={item.labId ?? `${item.selectionId}_${item.selectVersionId}`} data={item} />
        ))}
        {!autoSelectData.selections.data.filter((row) => !row.recommendation).length && (
          <div className={styles.noResultsText}>{autoSelectData.selections.emptyMessage}</div>
        )}
      </>
    );
  }, [
    autoSelectData.isLoading,
    autoSelectData.selections.data,
    autoSelectData.selections.emptyMessage,
    showOmakasePlanBanner,
    handleBannerClick,
    isReadyStart,
    omakasePlanIsLoading,
  ]);

  // TODO タグによって、表示非表示の制御。adminバックエンド側の実装をまち
  const tabItem = useMemo(() => {
    return (
      <div className={styles.toolArea}>
        <div className={styles.itemButtonsArea}>
          <ItemButtons
            className={styles.itemButtons}
            noScrollClassName={styles.noScrollItemButtons}
            options={autoSelectData.sort.options}
            activeId={autoSelectData.sort.get}
            onChange={autoSelectData.sort.set}
          />
        </div>
        <div className={styles.searchCondition}>
          <CardDescription />
          <div className={styles.tools}>
            <FilterCondition />
            <div className={styles.sort}>
              <Dropdown
                iconName="sort"
                minWidth={55}
                offsetTop={headerHeight}
                items={autoSelectData.filter.options}
                selected={autoSelectData.filter.get}
                onChange={autoSelectData.filter.set}
                labelClassName={styles.innerLabelWrapper}
                selectedLabel={autoSelectData.filter.getLabel}
              />
            </div>
            <SearchButton autoSelectData={autoSelectData} />
          </div>
        </div>
        <div className={styles.selectionArea}>{selectionItems}</div>
      </div>
    );
  }, [autoSelectData, headerHeight, selectionItems]);

  const tabItems = useMemo(() => {
    return autoSelectData.tabs.options.map((options) => ({ ...options, children: tabItem }));
  }, [autoSelectData.tabs.options, tabItem]);

  const handleResize = useCallback(
    ({ width }) => {
      const length = tabItems?.length ?? 0;
      if (length > 0) {
        setTabWidth((width ?? 0) / length);
      }
    },
    [tabItems],
  );

  return (
    <div className={classNames(styles.container)}>
      <div className={styles.tabArea}>
        {autoSelectData.tabs.get != null && (
          <Tabs
            type="line"
            tabMinWidth={tabWidth}
            scrollWidth={tabWidth}
            items={tabItems}
            activeKey={autoSelectData.tabs.get}
            onChange={autoSelectData.tabs.set}
            onReady={handleResize}
            onResize={handleResize}
          />
        )}
      </div>
    </div>
  );
});
